import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataExchange } from './queryKeys';

type CreateEmailWithRegistratonLinkResponse = {
  dataExchangeId: UUID;
  id: UUID;
  lastAccess?: string;
  link: string;
  status: 'expired' | 'created' | 'filled';
  stillValidFor?: string;
  validFor: string;
};

/**
 * Сreates an email with a registration link for a specified data exchange.
 *
 * The API generates a registration link associated with the given `dataExchangeId`.
 * The response includes metadata about the link such as its validity and access history.
 *
 * @param {UUID} dataExchangeId - The unique identifier of the data exchange to associate with the registration link.
 * @returns {Promise<CreateEmailWithRegistratonLinkResponse>} - A promise that resolves to the API response.
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/add_contact_link_data_exchange_contact_link_post
 *
 * @throws Will throw an error if the API call fails.
 */
export const createEmailWithRegistrationLink = async (dataExchangeId: UUID) => {
  const response = await vestigasApi
    .post(ENDPOINT.DATA_EXCHANGE.CREATE_EMAIL_WITH_REGISTRATION_LINK(), {
      json: { dataExchangeId },
    })
    .json<CreateEmailWithRegistratonLinkResponse>();

  return response;
};

/**
 * Custom hook for creating email with a registration link using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateEmailWithRegistrationLink = (
  options?: UseMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dataExchangeId: UUID) =>
      createEmailWithRegistrationLink(dataExchangeId),
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: queryKeysDataExchange.getAllSuppliers(undefined),
      });
    },
    ...options,
  });
};
